/**
 * OrgBendableEdge - 꺾기 포인트가 있는 커스텀 엣지 (V106)
 *
 * **기능:**
 * - bendPoints 배열로 꺾이는 선 경로 렌더링
 * - 편집 모드에서 컨트롤 포인트 드래그로 위치 조정
 * - 더블클릭으로 새 포인트 추가
 * - Delete 키로 선택된 포인트 삭제
 *
 * **데이터 모델:**
 * edge.data.bendPoints: Array<{ x: number; y: number }>
 * edge.data.isEditMode: boolean
 * edge.data.onBendPointsChange: (edgeId: string, newPoints: Array<{x,y}>) => void
 */

import React, { useCallback, useState, useEffect, useMemo } from 'react';
import {
  EdgeProps,
  getBezierPath,
  getSmoothStepPath,
  EdgeLabelRenderer,
  useReactFlow,
} from '@xyflow/react';

/** 그리드 스냅 크기 */
const GRID_SIZE = 20;

/** 컨트롤 포인트 크기 */
const CONTROL_POINT_SIZE = 12;

/** V114: 세로 오프셋 조절 단위 (px) */
const VERTICAL_OFFSET_STEP = 20;

/** BendPoint 타입 */
interface BendPoint {
  x: number;
  y: number;
}

/** Edge 데이터 확장 */
interface BendableEdgeData extends Record<string, unknown> {
  bendPoints?: BendPoint[];
  isEditMode?: boolean;
  onBendPointsChange?: (edgeId: string, newPoints: BendPoint[]) => void;
  /** V114: 세로 연결선 오프셋 (px) */
  verticalOffset?: number;
  /** V114: 세로 오프셋 변경 콜백 */
  onVerticalOffsetChange?: (edgeId: string, newOffset: number) => void;
}

/**
 * 그리드 스냅 함수
 */
function snapToGrid(value: number, gridSize: number = GRID_SIZE): number {
  return Math.round(value / gridSize) * gridSize;
}

/**
 * SVG path 문자열 생성 (시작점 → bendPoints → 끝점)
 */
function createPathWithBendPoints(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
  bendPoints: BendPoint[]
): string {
  if (bendPoints.length === 0) {
    // 꺾이점이 없으면 직선
    return `M ${sourceX} ${sourceY} L ${targetX} ${targetY}`;
  }

  // 시작점 → 꺾이점들 → 끝점을 직선으로 연결
  let path = `M ${sourceX} ${sourceY}`;
  for (const point of bendPoints) {
    path += ` L ${point.x} ${point.y}`;
  }
  path += ` L ${targetX} ${targetY}`;

  return path;
}

/**
 * 두 점 사이의 중간점 계산
 */
function getMidpoint(p1: { x: number; y: number }, p2: { x: number; y: number }): { x: number; y: number } {
  return {
    x: (p1.x + p2.x) / 2,
    y: (p1.y + p2.y) / 2,
  };
}

/**
 * OrgBendableEdge 컴포넌트
 */
export function OrgBendableEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  data,
  selected,
}: EdgeProps) {
  const edgeData = data as BendableEdgeData | undefined;
  const bendPoints = edgeData?.bendPoints || [];
  const isEditMode = edgeData?.isEditMode || false;
  const onBendPointsChange = edgeData?.onBendPointsChange;
  // V114: 세로 오프셋
  const verticalOffset = edgeData?.verticalOffset || 0;
  const onVerticalOffsetChange = edgeData?.onVerticalOffsetChange;

  const { getZoom, screenToFlowPosition } = useReactFlow();

  // 드래그 중인 포인트 인덱스
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // V114: verticalOffset 적용된 경로 계산
  // bendPoints가 있으면 커스텀 path, 없으면 smoothstep 기반 + offset
  const edgePath = useMemo(() => {
    if (bendPoints.length > 0) {
      // bendPoints가 있으면 그대로 사용 (offset 미적용 - bendPoints가 우선)
      return createPathWithBendPoints(sourceX, sourceY, targetX, targetY, bendPoints);
    }

    // V114: verticalOffset 적용된 smoothstep 스타일 path
    // TB 레이아웃 기준: 부모 하단 → 중간 수평선 → 자식 상단
    // 중간 Y = (sourceY + targetY) / 2 + verticalOffset
    const midY = (sourceY + targetY) / 2 + verticalOffset;

    // smoothstep 스타일: 수직 → 수평 → 수직
    return `M ${sourceX} ${sourceY} L ${sourceX} ${midY} L ${targetX} ${midY} L ${targetX} ${targetY}`;
  }, [sourceX, sourceY, targetX, targetY, bendPoints, verticalOffset]);

  /**
   * 컨트롤 포인트 드래그 시작
   */
  const handleControlPointMouseDown = useCallback(
    (e: React.MouseEvent, index: number) => {
      if (!isEditMode) return;
      e.stopPropagation();
      e.preventDefault();

      setDraggingIndex(index);

      // 마우스 위치와 포인트 위치의 오프셋 계산
      const point = bendPoints[index];
      const flowPos = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      setDragOffset({
        x: point.x - flowPos.x,
        y: point.y - flowPos.y,
      });
    },
    [isEditMode, bendPoints, screenToFlowPosition]
  );

  /**
   * 마우스 이동 (드래그 중)
   */
  useEffect(() => {
    if (draggingIndex === null) return;

    const handleMouseMove = (e: MouseEvent) => {
      const flowPos = screenToFlowPosition({ x: e.clientX, y: e.clientY });

      // 그리드 스냅 적용
      const newX = snapToGrid(flowPos.x + dragOffset.x);
      const newY = snapToGrid(flowPos.y + dragOffset.y);

      // 새 bendPoints 배열 생성
      const newPoints = [...bendPoints];
      newPoints[draggingIndex] = { x: newX, y: newY };

      // 콜백 호출
      if (onBendPointsChange) {
        onBendPointsChange(id, newPoints);
      }
    };

    const handleMouseUp = () => {
      setDraggingIndex(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingIndex, dragOffset, bendPoints, id, onBendPointsChange, screenToFlowPosition]);

  /**
   * 엣지 더블클릭 → 새 포인트 추가
   */
  const handleEdgeDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      if (!isEditMode || !onBendPointsChange) return;
      e.stopPropagation();

      // 클릭 위치를 Flow 좌표로 변환
      const flowPos = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      const newPoint = {
        x: snapToGrid(flowPos.x),
        y: snapToGrid(flowPos.y),
      };

      // 기존 포인트들 사이에서 가장 가까운 위치에 삽입
      // 간단히: 끝에 추가
      const newPoints = [...bendPoints, newPoint];

      // 시작점-끝점 기준으로 정렬 (Y 기준, TB 레이아웃 가정)
      newPoints.sort((a, b) => {
        // 시작점과 가까운 순서로 정렬
        const distA = Math.abs(a.y - sourceY) + Math.abs(a.x - sourceX);
        const distB = Math.abs(b.y - sourceY) + Math.abs(b.x - sourceX);
        return distA - distB;
      });

      onBendPointsChange(id, newPoints);
    },
    [isEditMode, onBendPointsChange, id, bendPoints, screenToFlowPosition, sourceX, sourceY]
  );

  /**
   * 컨트롤 포인트 더블클릭 → 삭제
   */
  const handleControlPointDoubleClick = useCallback(
    (e: React.MouseEvent, index: number) => {
      if (!isEditMode || !onBendPointsChange) return;
      e.stopPropagation();

      const newPoints = bendPoints.filter((_, i) => i !== index);
      onBendPointsChange(id, newPoints);
    },
    [isEditMode, onBendPointsChange, id, bendPoints]
  );

  /**
   * V114.1: 세로 오프셋 증가 (+20px, Shift: +50px)
   */
  const handleIncreaseOffset = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (!onVerticalOffsetChange) return;
      const step = e.shiftKey ? 50 : VERTICAL_OFFSET_STEP;
      onVerticalOffsetChange(id, verticalOffset + step);
    },
    [id, verticalOffset, onVerticalOffsetChange]
  );

  /**
   * V114.1: 세로 오프셋 감소 (-20px, Shift: -50px)
   */
  const handleDecreaseOffset = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (!onVerticalOffsetChange) return;
      const step = e.shiftKey ? 50 : VERTICAL_OFFSET_STEP;
      onVerticalOffsetChange(id, verticalOffset - step);
    },
    [id, verticalOffset, onVerticalOffsetChange]
  );

  // V114: 엣지 중간점 (오프셋 조절 버튼 위치)
  const midY = (sourceY + targetY) / 2 + verticalOffset;
  const midX = (sourceX + targetX) / 2;

  return (
    <>
      {/* 메인 엣지 경로 */}
      <path
        id={id}
        style={{
          ...style,
          strokeWidth: selected ? 3 : 2,
          stroke: selected ? '#3b82f6' : '#94a3b8',
        }}
        className="react-flow__edge-path"
        d={edgePath}
        markerEnd={markerEnd}
        onDoubleClick={handleEdgeDoubleClick}
      />

      {/* 투명한 클릭 영역 (더 넓은 히트 영역) */}
      <path
        style={{
          strokeWidth: 20,
          stroke: 'transparent',
          fill: 'none',
          cursor: isEditMode ? 'pointer' : 'default',
        }}
        d={edgePath}
        onDoubleClick={handleEdgeDoubleClick}
      />

      {/* 편집 모드에서 컨트롤 포인트 표시 */}
      {/* V108: EdgeLabelRenderer는 flow 좌표계를 사용하므로 transform으로 위치 지정 */}
      {isEditMode && (selected || bendPoints.length > 0) && (
        <EdgeLabelRenderer>
          {bendPoints.map((point, index) => (
            <div
              key={`${id}-control-${index}`}
              style={{
                position: 'absolute',
                // V108: EdgeLabelRenderer 내에서는 transform으로 flow 좌표 적용
                // left/top 대신 transform만 사용해야 pan/zoom에 정확히 동기화됨
                transform: `translate(${point.x - CONTROL_POINT_SIZE / 2}px, ${point.y - CONTROL_POINT_SIZE / 2}px)`,
                width: CONTROL_POINT_SIZE,
                height: CONTROL_POINT_SIZE,
                background: draggingIndex === index ? '#2563eb' : '#3b82f6',
                border: '2px solid white',
                borderRadius: '50%',
                cursor: 'move',
                pointerEvents: 'all',
                zIndex: 1000,
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              }}
              onMouseDown={(e) => handleControlPointMouseDown(e, index)}
              onDoubleClick={(e) => handleControlPointDoubleClick(e, index)}
              title="드래그: 이동 / 더블클릭: 삭제"
            />
          ))}

          {/* 선택된 상태에서 "더블클릭으로 포인트 추가" 힌트 */}
          {selected && bendPoints.length === 0 && !onVerticalOffsetChange && (
            <div
              style={{
                position: 'absolute',
                // V108: 힌트 라벨도 transform으로 위치 지정
                transform: `translate(${(sourceX + targetX) / 2}px, ${(sourceY + targetY) / 2}px) translate(-50%, -50%)`,
                background: 'rgba(59, 130, 246, 0.9)',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '11px',
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
              }}
            >
              더블클릭: 꺾기 포인트 추가
            </div>
          )}

          {/* V114.1: 세로 오프셋 조절 버튼 - 크기 확대 */}
          {selected && bendPoints.length === 0 && onVerticalOffsetChange && (
            <div
              className="nodrag nopan"
              style={{
                position: 'absolute',
                transform: `translate(${midX}px, ${midY}px) translate(-50%, -50%)`,
                display: 'flex',
                gap: '8px',
                pointerEvents: 'all',
                zIndex: 9999,
              }}
            >
              {/* 위로 (-) 버튼: 중간선을 위로 이동 */}
              <button
                className="nodrag nopan"
                onClick={handleDecreaseOffset}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '6px',
                  border: '2px solid #3b82f6',
                  background: 'white',
                  color: '#1e40af',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(59, 130, 246, 0.4)',
                }}
                title="세로 간격 줄이기 (-20px, Shift: -50px)"
              >
                −
              </button>
              {/* 아래로 (+) 버튼: 중간선을 아래로 이동 */}
              <button
                className="nodrag nopan"
                onClick={handleIncreaseOffset}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '6px',
                  border: '2px solid #3b82f6',
                  background: 'white',
                  color: '#1e40af',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(59, 130, 246, 0.4)',
                }}
                title="세로 간격 늘리기 (+20px, Shift: +50px)"
              >
                +
              </button>
            </div>
          )}
        </EdgeLabelRenderer>
      )}
    </>
  );
}

export default OrgBendableEdge;
